import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, GraduationCap, Lightbulb } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/format";
import { TRAINING, type TrainingModule } from "@/lib/training";
import { PageHeader } from "@/components/app/PageHeader";

function ModuleBody({ m, done, onToggle }: { m: TrainingModule; done: boolean; onToggle: () => void }) {
  return (
    <div className="space-y-5 pb-2">
      <ol className="space-y-3">
        {m.steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm text-slate-300">
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-orange-500/40 bg-orange-500/10 font-mono text-xs text-orange-300">{i + 1}</span>
            <span className="pt-0.5">{s}</span>
          </li>
        ))}
      </ol>
      {m.tips?.map((t, i) => (
        <p key={i} className="flex gap-2 rounded-md border border-cyan-500/20 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-200"><Lightbulb className="h-4 w-4 flex-none" />{t}</p>
      ))}
      <div className="flex flex-wrap gap-2">
        {m.link && <Button asChild size="sm" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"><Link to={m.link.to} data-testid={`training-link-${m.id}`}>{m.link.label}</Link></Button>}
        <Button size="sm" data-testid={`training-done-${m.id}`} onClick={onToggle} className={done ? "bg-emerald-600 text-white hover:bg-emerald-500" : "bg-blue-600 text-white hover:bg-blue-500"}>
          {done ? "Module terminé" : "Marquer comme terminé"}
        </Button>
      </div>
    </div>
  );
}

export default function TrainingPage() {
  const { user } = useAuth();
  const role = user ? user.role : "editor";
  const key = `innova-training-${user ? user.id : "anon"}`;
  const modules = TRAINING.filter((m) => m.roles.includes(role));
  const [done, setDone] = useState<string[]>([]);

  useEffect(() => { setDone(JSON.parse(localStorage.getItem(key) || "[]")); }, [key]);
  const toggle = (id: string) => {
    const next = done.includes(id) ? done.filter((x) => x !== id) : [...done, id];
    setDone(next);
    localStorage.setItem(key, JSON.stringify(next));
  };
  const count = modules.filter((m) => done.includes(m.id)).length;

  return (
    <div>
      <PageHeader eyebrow={`Profil ${ROLE_LABELS[role]}`} title="Formation" description="Guides pas à pas adaptés à votre rôle pour bien utiliser Innova-Sign Cloud." />
      <div className="panel mb-6 flex flex-col gap-4 p-6 md:flex-row md:items-center">
        <GraduationCap className="h-8 w-8 text-orange-400" />
        <div className="flex-1">
          <p className="text-sm text-slate-300"><span data-testid="training-progress" className="font-mono text-slate-100">{count} / {modules.length}</span> modules terminés</p>
          <Progress value={modules.length ? (count / modules.length) * 100 : 0} className="mt-2 h-2 bg-slate-800" />
        </div>
      </div>
      <Accordion type="single" collapsible defaultValue={modules[0]?.id} className="space-y-3" data-testid="training-modules">
        {modules.map((m, i) => (
          <AccordionItem key={m.id} value={m.id} data-testid={`training-module-${m.id}`} className="panel border-b-0 px-6">
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                {done.includes(m.id) ? <CheckCircle2 className="h-5 w-5 flex-none text-emerald-400" /> : <Circle className="h-5 w-5 flex-none text-slate-600" />}
                <span><span className="block font-display text-base font-medium text-slate-100">{i + 1}. {m.title}</span><span className="block text-xs font-normal text-slate-500">{m.summary}</span></span>
              </span>
            </AccordionTrigger>
            <AccordionContent><ModuleBody m={m} done={done.includes(m.id)} onToggle={() => toggle(m.id)} /></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
